/* THE SANDBOX ROOM, AS DATA RATHER THAN AS A SIDE EFFECT.
 *
 * This lived inside `playground.js`'s `buildRoom`, which also touches the
 * DOM, so nothing headless could check it — and it shipped a room with no
 * way out of it. `canLeaveFrom` floods on tiles the body fits in: every
 * border tile was WALL so the flood never reached the border to consider it,
 * there was no mouth or stair inside either, and the answer was false at
 * every tile in the arena. `Player.update` reverts a launch that ends
 * somewhere you cannot leave, so the one stunt the room exists to demonstrate
 * — `throw me` — snapped the player back to the pad every single time.
 * Reported from play as the pod damage boost resetting position.
 *
 * Split out so `test/soak.ts` paints the same room the page paints and can
 * assert the property directly. A copy of the layout in the test would be a
 * second source of truth and would drift; this is the only one.
 */
import { T, TS } from '../src/art.ts';

/** One of everything worth trying, along the north wall. */
/** Is this screen the arena, or has the game baked its own world over the top?
 *
 * THE FIRST VERSION OF THIS ASKED WHETHER THE MIDDLE OF THE FLOOR WAS GROUND, and ground is the most
 * ordinary tile in the world -- so on any wild screen with ground in the middle the answer was yes,
 * the bench decided its room was fine, and it went on running in whatever country the game had
 * loaded. Spawning worked, because that ground is walkable, which made it look mended. Ask for two
 * things only the arena has instead: a cliff in the corner, and the pond on the left. */
export function isArena(t): boolean {
  return !!t && !!t[0] && t[0][0] === T.CLIFF && !!t[6] && t[6][1] === T.WATER;
}

export const FEATURES = [T.RUBBLE, T.THORN, T.CRACK, T.BRAZIER, T.PUSH, T.NEST];

/** Where the arena puts the player, in pixels. */
export const START = [8 * TS, 7 * TS];

/** Paint the sandbox arena into `t`, a COLS x ROWS tile grid. */
export function paintArena(t, cols, rows) {
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const edge = x === 0 || y === 0 || x === cols - 1 || y === rows - 1;
      /* GROUND AND CLIFF, NOT FLOOR AND WALL (2026-09-08). This room was painted entirely in
       * `T.FLOOR` inside a border of `T.WALL`, and BOTH ARE RETIRED IDS -- the hollows' tiles, kept
       * only so the live ones keep their numbers (`art.ts:10`). Neither is walkable, so the whole
       * bench was a floor nothing could stand on: every spawn answered "no room for a shellback",
       * which is what a place to test everything looked like when nothing could be put in it. */
      t[y][x] = edge ? T.CLIFF : T.GROUND;
    }
  }
  // a pond on the left, so the Lung has something to be for
  for (let y = 6; y < 10; y++) {
    for (let x = 1; x < 5; x++) t[y][x] = (x === 4 || y === 9) ? T.SHALLOW : T.WATER;
  }
  // a hedge running across, so fire has somewhere to go
  for (let x = 7; x < 13; x++) t[3][x] = T.THORN;
  FEATURES.forEach((k, i) => { t[1][2 + i * 2] = k; });
  // A WAY OUT. See the note at the top of this file: without it the room
  // cancels every launch taken inside it. A real hollow room always has a
  // door in its wall, so the game itself was never affected — the sandbox
  // was the only room in the project with no exit at all.
  t[rows - 2][cols - 3] = T.RAMP;   // STAIRS is retired too; a ramp is the live way up, and it sounds like stone underfoot
  return t;
}
